<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            class="statement-irs1099-grid"
            :columnDefs="columnDefs"
            :rowData="irs1099Data ? irs1099Data.owners : []"
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Statement generation can take up to one minute, please be patient...</span>" 
            :getRowClass="getRowClass"
            :isAutoWidth="true"
            :header-height="50"
            :loading="!irs1099Data"
            :getRowId="(params) => (params.data && params.data.owner_id ? params.data.owner_id.toString() : '')"
            :isSearch="false"
            grid-theme="ag-theme-alpine-owner-statement"
        >
            <responsive-button :icon="['far', 'file-pdf']" variant="primary" @click="handleDownloadPDF" :busy="pdfBusy"
                >Download PDF</responsive-button
            >
            <responsive-button
                :icon="['fas', 'file-excel']"
                variant="primary"
                @click="handleDownloadExcel"
                :busy="excelBusy"
                >Download Excel</responsive-button
            >
        </grid>
    </page>
</template>

<script>
    import API from '../../api';
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import grid from '../generic/grid/grid.vue';
    import Page from '../generic/page/page.vue';
    import { OWNER_STATEMENT_GENERATION_ENDPOINT } from '../../endpoints';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import { convertBlobResponseToJson, getFilenameFromResponse, formatResponseError } from '../../utils/api';
    import { saveAs } from 'file-saver';
    import MultiLineHeader from '../common/multi-line-header.vue';

    export default {
        name: 'OwnerStatementGenerationIRS1099',
        components: { grid, Page, ResponsiveButton, MultiLineHeader },
        data() {
            return {
                busy: false,
                irs1099Data: null,
                pdfBusy: false,
                excelBusy: false,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            tabs() {
                return [
                    { title: 'Generation', active: false, route: '/app/owner-statement/list' },
                    { title: `IRS 1099: ${this.irs1099Data ? this.irs1099Period : ''}`, active: true },
                ];
            },
            fromDate() {
                return this.$route.query.from_date;
            },
            toDate() {
                return this.$route.query.to_date;
            },
            generationIds() {
                return this.$route.query.generation_ids;
            },
            ownerIds() {
                return this.$route.query.owner_ids;
            },
            propertyIds() {
                return this.$route.query.property_ids;
            },
            irs1099Period() {
                const formattedFrom = format(parseISO(this.irs1099Data.from_date), this.userUnicodeDateFormat);
                const formattedTo = format(parseISO(this.irs1099Data.to_date), this.userUnicodeDateFormat);
                return `${formattedFrom} - ${formattedTo}`;
            },
            bodyParams() {
                return {
                    from_date: this.fromDate,
                    to_date: this.toDate,
                    generation_ids: this.generationIds ? this.generationIds.split(',') : [],
                    owner_ids: this.ownerIds ? this.ownerIds.split(',') : [],
                    property_ids: this.propertyIds ? this.propertyIds.split(',') : [],
                };
            },
            columnDefs() {
                return [
                    {
                        field: 'owner_name',
                        headerName: 'Owner',
                        sortable: true,
                    },
                    {
                        field: 'owner_company',
                        headerName: 'Company',
                        sortable: true,
                    },
                    {
                        field: 'owner_address',
                        headerName: 'Address',
                        sortable: true,
                    },
                    {
                        field: 'owner_tax_id',
                        headerName: 'EIN/SSN',
                        sortable: true,
                    },
                    {
                        headerName: 'Gross Rent',
                        headerComponent: 'MultiLineHeader',
                        headerComponentParams: {
                            items: ['Gross', 'Rent'],
                            itemClass: 'text-left',
                        },
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_rental_price'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                        autoWidthExclude: true,
                    },
                    {
                        headerName: 'Mgmt Comm',
                        headerComponent: 'MultiLineHeader',
                        headerComponentParams: {
                            items: ['Mgmt', 'Comm'],
                            itemClass: 'text-left',
                        },
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_commission', true),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                        autoWidthExclude: true,
                    },
                    {
                        headerName: 'Paid to Owner',
                        headerComponent: 'MultiLineHeader',
                        headerComponentParams: {
                            items: ['Paid to', 'Owner'],
                            itemClass: 'text-left',
                        },
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_balance_due_owner'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                        autoWidthExclude: true,
                    },
                ];
            },
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.onMount();
        },
        methods: {
            async onMount() {
                this.busy = true;
                try {
                    await this.generateIRS1099();
                } catch {
                } finally {
                    this.busy = false;
                }
            },
            async generateIRS1099() {
                try {
                    const { data } = await API.post(
                        OWNER_STATEMENT_GENERATION_ENDPOINT() + 'generate-irs1099/',
                        this.bodyParams
                    );
                    this.irs1099Data = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not generate irs 1099.' });
                    throw e;
                }
            },
            getAmountColumnValue(params, fieldName, is_negative) {
                return is_negative ? `-${params.data.totals[0][fieldName]}` : params.data.totals[0][fieldName];
            },
            getRowClass({ data }) {
                if (data.owner_name) {
                    return 'lx-owner-statement-row';
                }
                return '';
            },
            async handleDownloadPDF() {
                try {
                    this.pdfBusy = true;
                    const response = await API.post(
                        OWNER_STATEMENT_GENERATION_ENDPOINT() + 'irs1099-pdf/',
                        this.bodyParams,
                        { responseType: 'blob' }
                    );
                    saveAs(response.data, getFilenameFromResponse(response));
                } catch (e) {
                    if (e.response) {
                        e.response.data = await convertBlobResponseToJson(e.response);
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not download owner statement summary pdf.' });
                } finally {
                    this.pdfBusy = false;
                }
            },
            async handleDownloadExcel() {
                try {
                    this.excelBusy = true;
                    const response = await API.post(
                        OWNER_STATEMENT_GENERATION_ENDPOINT() + 'irs1099-excel/',
                        this.bodyParams,
                        { responseType: 'blob' }
                    );
                    saveAs(response.data, getFilenameFromResponse(response));
                } catch (e) {
                    if (e.response) {
                        e.response.data = await convertBlobResponseToJson(e.response);
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not download owner statement summary excel.' });
                } finally {
                    this.excelBusy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .statement-irs1099-grid {
        :deep(.ag-row) {
            overflow: visible !important;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
        }
    }
</style>
